<template>
  <vue-final-modal
    v-model="isOpen"
    classes="modal-overlay-container"
    content-class="modern-modal-content"
  >
    <div class="modal-header">
      <div class="header-text-container">
        <div class="title-wrapper">
          <div class="title-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="17 8 12 3 7 8"></polyline>
              <line x1="12" y1="3" x2="12" y2="15"></line>
            </svg>
          </div>
          <h5 class="modal-title">{{ isEdit ? 'Edit' : 'Upload' }} Training</h5>
        </div>
      </div>
      <button type="button" class="close-button" @click="closeModal" aria-label="Close">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>
    </div>

    <div class="modal-body custom-scroll">

      <div class="setting-item">
        <label>Training Type</label>
        <div class="type-toggle-container">
          <button class="type-btn" :class="{ active: form.type === 'scorm' }" @click="setType('scorm')" :disabled="isEdit">
            <i class="fas fa-laptop-code me-2"></i> Interactive (SCORM)
          </button>
          <button class="type-btn" :class="{ active: form.type === 'video' }" @click="setType('video')" :disabled="isEdit">
            <i class="fas fa-play-circle me-2"></i> Standard Video
          </button>
        </div>
        <small v-if="isEdit" class="text-muted mt-1 d-block" style="font-size: 0.75rem;">
          Training type cannot be changed during editing.
        </small>
      </div>

      <div class="setting-item mt-4">
        <label for="moduleName">Training Name <span class="text-danger">*</span></label>
        <div class="input-with-icon">
          <div class="input-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
            </svg>
          </div>
          <input
            id="moduleName"
            v-model="form.name"
            type="text"
            class="form-control"
            :class="{ 'border-danger': v$.name.$error }"
            placeholder="e.g. Phishing Awareness 101"
            @blur="v$.name.$touch()"
          />
        </div>
        <div class="text-danger mt-1 ms-1" v-for="error of v$.name.$errors" :key="error.$uid">
          <div style="font-size: 0.75rem; font-weight: 600;">{{ error.$message }}</div>
        </div>
      </div>

      <div class="setting-item" v-if="form.type === 'scorm'">
        <label for="scormVersion">SCORM Version</label>
        <div class="input-with-icon">
          <div class="input-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="16" x2="12" y2="12"></line>
              <line x1="12" y1="8" x2="12.01" y2="8"></line>
            </svg>
          </div>
          <select id="scormVersion" class="form-control" v-model="form.scorm_version">
            <option value="1.2">SCORM 1.2</option>
            <option value="2004">SCORM 2004</option>
          </select>
        </div>
      </div>

      <div class="setting-item mt-4">
        <label>
          {{ form.type === 'scorm' ? 'SCORM Package (.zip)' : 'Video File (.mp4)' }}
          <span v-if="!isEdit" class="text-danger">*</span>
          <span v-if="isEdit" class="text-muted fw-normal" style="text-transform: none; font-size: 0.75rem;">(Optional - upload to replace)</span>
        </label>
        <div
          class="upload-zone"
          @click="triggerFileInput"
          @dragover.prevent="isDragging = true"
          @dragleave.prevent="isDragging = false"
          @drop.prevent="handleDrop"
          :class="{ 'is-dragging': isDragging, 'has-file': form.file, 'is-invalid': fileError }"
        >
          <input type="file" ref="fileInput" :accept="form.type === 'scorm' ? '.zip' : 'video/mp4,video/x-m4v,video/*'" class="d-none" @change="handleFileSelect">

          <div v-if="!form.file" class="upload-content text-center">
            <div class="upload-icon mb-2">
              <i v-if="form.type === 'scorm'" class="fas fa-file-archive text-secondary fa-2x"></i>
              <i v-else class="fas fa-file-video text-secondary fa-2x"></i>
            </div>
            <p class="mb-1 fw-bold text-dark">Click to upload or drag and drop</p>
            <p class="small text-muted mb-0">
              {{ form.type === 'scorm' ? 'ZIP folder containing SCORM content' : 'High quality MP4 video file' }} (Max 500MB)
            </p>
          </div>

          <div v-else class="upload-content text-center">
            <div class="upload-icon mb-2">
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#00a0c1" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
            </div>
            <p class="mb-1 fw-bold text-dark text-truncate px-3">{{ form.file.name }}</p>
            <p class="small text-muted mb-0">{{ (form.file.size / 1024 / 1024).toFixed(2) }} MB</p>
            <button class="btn btn-sm btn-light mt-3" @click.stop="removeFile">Remove File</button>
          </div>
        </div>
        <div class="text-danger mt-1 ms-1" v-if="fileError">
          <div style="font-size: 0.75rem; font-weight: 600;">{{ fileError }}</div>
        </div>
      </div>

      <div class="setting-item mt-4">
        <label>
          Display Thumbnail (.jpg, .png)
          <span class="text-muted fw-normal" style="text-transform: none; font-size: 0.75rem;">(Optional)</span>
        </label>
        <div class="upload-zone thumb-zone" @click="triggerThumbInput" @dragover.prevent="isDraggingThumb = true" @dragleave.prevent="isDraggingThumb = false" @drop.prevent="handleThumbDrop" :class="{ 'is-dragging': isDraggingThumb, 'has-file': form.thumbnail, 'is-invalid': thumbError }">
          <input type="file" ref="thumbnailInput" accept="image/png, image/jpeg, image/jpg, image/webp" class="d-none" @change="handleThumbSelect">
          <div v-if="!form.thumbnail" class="upload-content text-center">
            <div class="upload-icon mb-1">
              <i class="fas fa-image text-secondary" style="font-size: 1.5rem;"></i>
            </div>
            <p class="small text-muted mb-0">Drag & drop or click to upload thumbnail image (Max 5MB)</p>
          </div>
          <div v-else class="upload-content text-center">
            <p class="mb-1 fw-bold text-dark text-truncate px-3" style="font-size: 0.85rem;">{{ form.thumbnail.name }}</p>
            <p class="small text-muted mb-0">{{ (form.thumbnail.size / 1024 / 1024).toFixed(2) }} MB</p>
            <button class="btn btn-sm btn-light mt-1 py-1" style="font-size: 0.75rem;" @click.stop="removeThumb">Remove Image</button>
          </div>
        </div>
        <div class="text-danger mt-1 ms-1" v-if="thumbError">
          <div style="font-size: 0.75rem; font-weight: 600;">{{ thumbError }}</div>
        </div>
      </div>
      <div v-if="isSaving" class="mt-4 p-3 bg-light rounded border border-info">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <span class="small fw-bold text-dark">
            <i :class="isProcessingUpload ? 'fas fa-cogs text-warning' : 'fas fa-cloud-upload-alt text-info'" class="me-1"></i>
            {{ isProcessingUpload ? 'Upload complete! Processing and saving to cloud...' : 'Uploading... You can safely close this window.' }}
          </span>
          <span class="small fw-bold" :class="isProcessingUpload ? 'text-success' : 'text-theme'">{{ uploadProgress }}%</span>
        </div>
        <div class="progress" style="height: 10px; border-radius: 10px;">
          <div
            class="progress-bar progress-bar-striped progress-bar-animated"
            role="progressbar"
            :class="isProcessingUpload ? 'bg-success' : 'bg-theme'"
            :style="{ width: uploadProgress + '%' }"
            :aria-valuenow="uploadProgress"
            aria-valuemin="0"
            aria-valuemax="100"
          ></div>
        </div>
      </div>
    </div>



    <div class="modal-footer">
      <button type="button" class="btn btn-secondary" @click="closeModal">
        {{ isSaving ? 'Close Window' : 'Cancel' }}
      </button>

      <button
        type="button"
        class="btn btn-primary d-flex align-items-center"
        :disabled="!form.name || (!isEdit && !form.file) || isSaving"
        @click="handleSave"
      >
        <span v-if="isSaving" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
        {{ isSaving ? 'Uploading...' : (isEdit ? 'Save Changes' : 'Upload Training') }}
      </button>
    </div>
  </vue-final-modal>
</template>

<script setup>
import { reactive, ref, computed, watch, defineEmits, defineProps } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required, helpers } from '@vuelidate/validators';

const props = defineProps(['modelValue', 'isEdit', 'initialData', 'isSaving', 'uploadProgress', 'isProcessingUpload']);
const emit = defineEmits(['update:modelValue', 'save']);

const fileInput = ref(null);
const thumbnailInput = ref(null);
const isDragging = ref(false);
const isDraggingThumb = ref(false);

const fileError = ref('');
const thumbError = ref('');

const MAX_FILE_SIZE_MB = 500;
const MAX_THUMB_SIZE_MB = 5;

const isOpen = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val)
});

const form = reactive({
  type: 'scorm',
  name: '',
  scorm_version: '2004',
  file: null,
  thumbnail: null
});

// 🚨 Vuelidate Rules
const rules = computed(() => ({
  name: {
    required: helpers.withMessage('Training Name is required.', required)
  }
}));

const v$ = useVuelidate(rules, form);

watch(() => props.modelValue, (newVal) => {
  if (newVal) {
    if (props.isEdit && props.initialData) {
      form.type = props.initialData.type || 'scorm';
      form.name = props.initialData.name || '';
      form.scorm_version = props.initialData.scorm_version || '2004';
      form.file = null;
      form.thumbnail = null;
    } else {
      form.type = 'scorm';
      form.name = '';
      form.scorm_version = '2004';
      form.file = null;
      form.thumbnail = null;
    }
    isDragging.value = false;
    isDraggingThumb.value = false;

    // Reset all validation errors when modal opens
    v$.value.$reset();
    fileError.value = '';
    thumbError.value = '';

    if (fileInput.value) fileInput.value.value = '';
    if (thumbnailInput.value) thumbnailInput.value.value = '';
  }
});

const setType = (newType) => {
  if (props.isEdit) return;
  if (form.type !== newType) {
    form.type = newType;
    form.file = null;
    fileError.value = ''; // Clear file error on type switch
    if (fileInput.value) fileInput.value.value = '';
  }
};

const triggerFileInput = () => { if (!form.file) fileInput.value.click(); };
const handleFileSelect = (event) => { const file = event.target.files[0]; if (file) processFile(file); };
const handleDrop = (event) => { isDragging.value = false; const file = event.dataTransfer.files[0]; if (file) processFile(file); };

const processFile = (file) => {
  fileError.value = ''; // Reset error state

  // Validation: Check Max File Size
  if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
    fileError.value = `File is too large! Maximum allowed size is ${MAX_FILE_SIZE_MB}MB.`;
    if (fileInput.value) fileInput.value.value = '';
    return;
  }

  if (form.type === 'scorm') {
    if (file.name.toLowerCase().endsWith('.zip')) {
      form.file = file;
      if (!form.name) form.name = file.name.replace('.zip', '');
    } else {
      fileError.value = "Please upload a valid .zip SCORM package.";
    }
  } else if (form.type === 'video') {
    if (file.type.startsWith('video/') || file.name.toLowerCase().endsWith('.mp4')) {
      form.file = file;
      if (!form.name) form.name = file.name.replace(/\.[^/.]+$/, "");
    } else {
      fileError.value = "Please upload a valid video file (e.g. .mp4).";
    }
  }
};

const triggerThumbInput = () => { if (!form.thumbnail) thumbnailInput.value.click(); };
const handleThumbSelect = (event) => { const file = event.target.files[0]; if (file) processThumb(file); };
const handleThumbDrop = (event) => { isDraggingThumb.value = false; const file = event.dataTransfer.files[0]; if (file) processThumb(file); };

const processThumb = (file) => {
  thumbError.value = ''; // Reset error state

  // Validation: Check Max Thumbnail Size
  if (file.size > MAX_THUMB_SIZE_MB * 1024 * 1024) {
    thumbError.value = `File is too large! Maximum allowed size is ${MAX_THUMB_SIZE_MB}MB.`;
    if (thumbnailInput.value) thumbnailInput.value.value = '';
    return;
  }

  if (file.type.startsWith('image/')) {
    form.thumbnail = file;
  } else {
    thumbError.value = "Please select a valid image file type (.jpg, .png).";
  }
};

const removeFile = () => {
  form.file = null;
  fileError.value = ''; // Clear error when removing file
  if (fileInput.value) fileInput.value.value = '';
};

const removeThumb = () => {
  form.thumbnail = null;
  thumbError.value = ''; // Clear error when removing image
  if (thumbnailInput.value) thumbnailInput.value.value = '';
};

const closeModal = () => {
  isOpen.value = false;
  };

const handleSave = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return;

  emit('save', {
    type: form.type,
    name: form.name,
    scorm_version: form.type === 'scorm' ? form.scorm_version : null,
    file: form.file,
    thumbnail: form.thumbnail
  });
};
</script>

<style scoped>
:deep(.modal-overlay-container) { display: flex; justify-content: center; align-items: center; background-color: rgba(15, 23, 42, 0.6); backdrop-filter: blur(4px); z-index: 1000; }
:deep(.modern-modal-content) { background: white; border-radius: 12px; width: 100%; max-width: 500px; box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1); display: flex; flex-direction: column; border: none !important; padding: 0; margin: 0 1rem; overflow: hidden; max-height: 90vh; }

.modal-header { display: flex; justify-content: space-between; align-items: center; padding: 1rem 1.5rem; border-bottom: 1px solid #f1f5f9; background-color: #ffffff; }
.title-wrapper { display: flex; align-items: center; gap: 0.75rem; }
.title-icon { color: #00a0c1; display: flex; align-items: center; justify-content: center; background: rgba(0, 160, 193, 0.1); padding: 8px; border-radius: 8px; }
.modal-title { color: #1e293b; margin: 0; font-size: 1.15rem; font-weight: 700; }
.close-button { background: #f1f5f9; border: none; width: 32px; height: 32px; border-radius: 50%; display: flex; align-items: center; justify-content: center; cursor: pointer; color: #94a3b8; transition: all 0.2s; }
.close-button:hover { background-color: #fee2e2; color: #dc3545; transform: rotate(90deg); }

/* Make the body scrollable in case the window is small */
.modal-body.custom-scroll { padding: 1.25rem 1.5rem; flex-grow: 1; overflow-y: auto; }

.setting-item { margin-bottom: 1.25rem; }
.setting-item label { display: block; font-size: 0.8rem; text-transform: uppercase; letter-spacing: 0.025em; color: #64748b; margin-bottom: 0.5rem; font-weight: 700; }
.input-with-icon { position: relative; display: flex; align-items: center; }
.input-icon { position: absolute; left: 12px; color: #94a3b8; display: flex; align-items: center; pointer-events: none; z-index: 1; }
.form-control {
  width: 100%;
  padding: 0.7rem 0.7rem 0.7rem 2.5rem;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.95rem;
  color: #1e293b;
  background-color: #ffffff;
  transition: all 0.2s;
  appearance: auto; /* 🚨 This brings back the dropdown arrow! */
}
.form-control:focus { border-color: #00a0c1; outline: none; box-shadow: 0 0 0 3px rgba(0, 160, 193, 0.15); }

.type-toggle-container { display: flex; background-color: #f1f5f9; border-radius: 8px; padding: 4px; gap: 4px; }
.type-btn { flex: 1; border: none; background: transparent; padding: 0.6rem; border-radius: 6px; font-size: 0.9rem; font-weight: 600; color: #64748b; cursor: pointer; transition: all 0.2s; }
.type-btn:hover:not(:disabled) { color: #1e293b; }
.type-btn.active { background-color: white; color: #00a0c1; box-shadow: 0 2px 4px rgba(0,0,0,0.05); }
.type-btn:disabled { opacity: 0.6; cursor: not-allowed; }

/* Upload Zones */
.upload-zone { border: 2px dashed #cbd5e1; border-radius: 8px; padding: 2rem 1rem; background-color: #f8fafc; cursor: pointer; transition: all 0.2s ease; }
.upload-zone:hover, .upload-zone.is-dragging { border-color: #00a0c1; background-color: #f0f9ff; }
.upload-zone.has-file { border-style: solid; border-color: #e2e8f0; background-color: #ffffff; cursor: default; }

/* Thumbnail Zone Specifics - make it a bit more compact */
.thumb-zone { padding: 1rem; }

.modal-footer { display: flex; justify-content: flex-end; border-top: 1px solid #f1f5f9; padding: 1rem 1.5rem; gap: 0.75rem; background-color: #f8fafc; }
.btn { padding: 0.5rem 1.25rem; border-radius: 8px; font-size: 0.9rem; font-weight: 600; cursor: pointer; transition: all 0.2s; border: 1px solid transparent; }
.btn-primary { background-color: #00a0c1; color: white; }
.btn-primary:hover:not(:disabled) { background-color: #008da8; }
.btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-secondary { background-color: white; color: #64748b; border-color: #e2e8f0; }
.btn-secondary:hover { background-color: #f8fafc; border-color: #cbd5e1; color: #1e293b; }
.btn-light { border-color: #e2e8f0; color: #dc3545; font-size: 0.8rem; }
.btn-light:hover { background-color: #fee2e2; border-color: #fca5a5; }

/* Invalid Upload Zone Styling */
.upload-zone.is-invalid {
  border-color: #dc3545;
  background-color: #fff5f5;
}
.upload-zone.is-invalid .upload-icon i {
  color: #dc3545 !important;
}
</style>